import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "../baseQuery";
import CryptoJS from 'crypto-js';
import md5 from 'md5';
import { format } from 'date-fns';
import pako from 'pako';
import { domainConfigs } from "../../../../../apps/web/src/config/domains";


// Support both Vite (import.meta.env) and Node (process.env) environments
const env =
  typeof import.meta !== "undefined" && import.meta.env
    ? import.meta.env
    : (typeof process !== "undefined" ? process.env : {}) as Record<string, string | undefined>;

const hostname = typeof window !== 'undefined' ? window.location.hostname : 'default';

const domainConfig = domainConfigs[hostname];

const encryptStatus =
  domainConfig?.encrypt;

const secret = 'flysyncflysync'; // Define your secret key

const headers = {
  'content-type': 'application/json; charset=utf-8',
  'x-client-type': '97f4', // CRC16
};

function uint8ArrayToWordArray(bytes: Uint8Array) {
  const words: number[] = [];

  for (let index = 0; index < bytes.length; index += 1) {
    words[index >>> 2] |= bytes[index] << (24 - (index % 4) * 8);
  }

  return CryptoJS.lib.WordArray.create(words, bytes.length);
}

function wordArrayToUint8Array(wordArray: CryptoJS.lib.WordArray) {
  const { words, sigBytes } = wordArray;
  const bytes = new Uint8Array(sigBytes);

  for (let index = 0; index < sigBytes; index += 1) {
    bytes[index] = (words[index >>> 2] >>> (24 - (index % 4) * 8)) & 0xff;
  }

  return bytes;
}

function getUserEmail() {
  return (
    JSON.parse(sessionStorage.getItem('user') || '{}').email ||
    'flysyncdev@gmail.com'
  );
}

function getKey() {
  return md5(getUserEmail() + format(new Date(), 'MMMM') + secret);
}

function encrypt(text: string) {
  const compressedText = pako.deflate(text);
  const compressedBase64 = CryptoJS.enc.Base64.stringify(
    uint8ArrayToWordArray(compressedText),
  );
  const iv = CryptoJS.lib.WordArray.random(16);
  const key = CryptoJS.enc.Utf8.parse(getKey());
  const encrypted = CryptoJS.AES.encrypt(compressedBase64, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return `${CryptoJS.enc.Base64.stringify(iv)}:${encrypted.ciphertext.toString(CryptoJS.enc.Base64)}`;
}

function decrypt(text: string) {
  const [ivText = '', encryptedText = ''] = JSON.stringify(text).split(':');
  const iv = CryptoJS.enc.Base64.parse(ivText);
  const ciphertext = CryptoJS.enc.Base64.parse(encryptedText);
  const key = CryptoJS.enc.Utf8.parse(getKey());
  const decrypted = CryptoJS.AES.decrypt(
    { ciphertext } as CryptoJS.lib.CipherParams,
    key,
    {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    },
  );
  const compressedBase64 = decrypted.toString(CryptoJS.enc.Utf8);
  const compressedBytes = wordArrayToUint8Array(
    CryptoJS.enc.Base64.parse(compressedBase64),
  );

  return pako.inflate(compressedBytes, { to: 'string' });
}

export const bookingModuleApi = createApi({
  reducerPath: "BookingApi",
  baseQuery: baseQuery,
  tagTypes: ["BookingApi"],
  endpoints: (build) => ({
    checkUserExists: build.mutation({
      query: (data) => ({
        // url: "user/checkUserExists",
        url: "api/user/checkUserExistsB2C",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    verifyUser: build.mutation({
      query: (data) => ({
        // url: "/user/signin",
        url: "api/user/signInB2C",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    sendEmailOtp: build.mutation({
      query: (data) => ({
        // url: "user/sendEmailOtp",
        url: "api/user/sendEmailOtpB2C",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    verifyOtp: build.mutation({
      query: (data) => ({
        // url: "user/verifyOTP",
        url: "api/user/verifyOTPB2C",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    searchCity: build.query({
      query: ({ searchTerm }) => ({
        url: "requestForm/search",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify({searchData: searchTerm}))} : {searchData: searchTerm},
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    searchFlight: build.mutation({
      query: ({ input }) => ({
        url: "flights/search",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(input))} : input,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    signIn: build.mutation({
      query: ({ data }) => ({
        // url: "user/signIn",
        url: "api/user/signInB2C",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    registerDropdown: build.mutation({
      query: ({ data }) => ({
        url: "user/registerDropdown",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    addToCart: build.mutation({
      query: ({ data, auth }) => ({
        url: 'addToCart/addToCart',
        method: 'POST',
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    fareRule: build.mutation({
      query: ({ data }) => ({
        url: "fareRule/fetchFareRule",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    getGst: build.mutation({
      query: ({ data }) => ({
        url: "booking/getGst",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    bookTicket: build.mutation({
      query: ({ data }) => ({
        url: "booking/book",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    getSeatMap: build.mutation({
      query: ({ data }) => ({
        url: "seatMap/seat",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    intiateBooking: build.mutation({
      query: ({ data, auth }) => ({
        url: "booking/initiate",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    request: build.mutation({
      query: ({ data }) => ({
        url: "init/request",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    viewBooking: build.mutation({
      query: ({ data, auth }) => ({
        url: "booking/view",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    confirmBooking: build.mutation({
      query: ({ data, auth }) => ({
        url: "booking/confirm",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    sendMail: build.mutation({
      query: ({ data, auth }) => ({
        url: "booking/sendSuccessMail",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    updateUser: build.mutation({
      query: (data) => ({
        url: "user/updateUserName",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    bookingList: build.mutation({
      query: ({ auth, data }) => ({
        url: "api/user/getAllBooking",
        method: "POST",
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    getCancellationCharges: build.mutation({
      query: ({ data, auth }) => ({
        url: "cancellation/getCancellationCharges",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    createUser: build.mutation({
      query: ({ data }) => ({
        url: "api/user/createUser",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    getPassengerDetails: build.mutation({
      query: ({ data, auth }) => ({
        url: "api/booking/getCancelPassenger",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    cancelBooking: build.mutation({
      query: ({ data, auth }) => ({
        url: "amendments/raise",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    getUserDetails: build.mutation({
      query: ({ auth, userId }) => ({
        url: "user/getUserDetails",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify({ userId }))} : { userId },
        headers: { 
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    generateSecurePaymentToken: build.mutation({
      query: ({ data, auth }) => ({
        url: "pg/generateSecurePaymentToken",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': auth || ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    getOrderDetails: build.mutation({
      query: ({ data, auth, securePaymentToken, pg_code }) => {
        // Support both legacy and secure flows
        if (securePaymentToken) {
          return {
            url: "pg/insertPGOrderDetails",
            method: "POST",
            body: { securePaymentToken, pg_code },
            headers: {
              ...headers,
              'Auth': auth || ''
            },
          };
        }
        // Legacy flow (existing)
        return {
          url: "pg/insertPGOrderDetails",
          method: "POST",
          body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
          headers: {
            ...headers,
            'Auth': auth || ''
          },
        };
      },
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    verifyRazorpayPayment: build.mutation({
      query: ({ data }) => ({
        url: "pg/rpPGResponse",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    sendErrorMail: build.mutation({
      query: ({ data }) => ({
        url: "booking/sendErrorMail",
        method: "POST",
        body: encryptStatus == 'Y' ? {data: encrypt(JSON.stringify(data))} : data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    }),
    getAtomScriptUrl: build.mutation({
      query: ({ data }) => ({
        url: "pg/getAtomScriptUrl",
        method: "POST",
        body: data,
        headers: {
          ...headers,
          'Auth': sessionStorage.getItem('token') ? `${sessionStorage.getItem('token')}` : ''
        },
      }),
      transformResponse: (response: string) => encryptStatus == 'Y' ? JSON.parse(decrypt(response)) : response,
      transformErrorResponse: (response) => response,
    })
  }),
});

export const {
  useLazySearchCityQuery,
  useSearchFlightMutation,
  useRegisterDropdownMutation,
  useAddToCartMutation,
  useFareRuleMutation,
  useGetGstMutation,
  useBookTicketMutation,
  useGetSeatMapMutation,
  useIntiateBookingMutation,
  useRequestMutation,
  useViewBookingMutation,
  useConfirmBookingMutation,
  useSendMailMutation,
  useSignInMutation,
  useCheckUserExistsMutation,
  useSendEmailOtpMutation,
  useVerifyUserMutation,
  useVerifyOtpMutation,
  useUpdateUserMutation,
  useBookingListMutation,
  useGetCancellationChargesMutation,
  useCreateUserMutation,
  useGetPassengerDetailsMutation,
  useCancelBookingMutation,
  useGetUserDetailsMutation,
  useGetOrderDetailsMutation,
  useGenerateSecurePaymentTokenMutation,
  useSendErrorMailMutation,
  useVerifyRazorpayPaymentMutation,
  useGetAtomScriptUrlMutation,
} = bookingModuleApi;

// export const selectSeatMap = bookingModuleApi.endpoints.getSeatMap.select;
